import React, { useCallback, useLayoutEffect, useState } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Text from 'design-system/Text';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import CardRow from 'design-system/CardRow';
import StatusBar from 'design-system/StatusBar';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SettingsSection } from 'design-system/SettingsSection';
import { SCROLL_INDICATOR_INSETS, rem } from 'design-system/values';
import { AppView, CardView, Spacer, withAnimatedNavigationHeader } from 'design-system';
import Links from 'utils/links';
import useColors from 'hooks/useColors';
import { registerUser } from 'actions/user';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { OnboardingFlow } from 'utils/types/navigation';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import { selectPrimaryAddressForGB } from 'reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import CardRowContainer from 'design-system/CardRowContainer';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import { useCommonInvestStyles } from './styles';
import { getRequiredNationalIdentifiers } from '../actions';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
import { selectHasCountryOfTaxResidence } from '../reducers/selectors';
const strings = {
    title: 'Title',
    dob: 'Date of birth',
    lastName: 'Legal last name',
    firstName: 'Legal first name',
    middleName: 'Legal middle name',
    countryOfCitizenship: 'Citizenships',
};
const ConfirmPersonalDetailsScreen = withAnimatedNavigationHeader(({ route, scrollHandler, navigation }) => {
    const colors = useColors();
    const commonStyles = useCommonInvestStyles();
    const store = useAppStore();
    const primaryAddress = useAppSelector(selectPrimaryAddressForGB);
    const navigateToNextScreen = useNavigateToNextScreen();
    const navigateToNextScreenForPots = useNavigateToNextScreenForPotsOnboarding();
    const [, , loading, fetch] = useFetchOld(muteErrorAndResult);
    const dispatch = useAppDispatch();
    const dob = route.params?.dob;
    const flow = route.params?.flow;
    const title = route.params?.title;
    const country = route.params?.country;
    const postData = route.params?.postData;
    const lastName = route.params?.lastName;
    const firstName = route.params?.firstName;
    const middleName = route.params?.middleName;
    const [checked, setChecked] = useState(false);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.white,
        });
    }, [colors.text.white]);
    useAmplitudeOnMountEvent(flow, 'User Info Confirmation Screen');
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const onDone = useCallback(async () => {
        try {
            const response = Object.keys(postData).length > 0 ? await fetch(registerUser(postData)) : true;
            if (response) {
                const countryOfTaxResidence = selectHasCountryOfTaxResidence(store.getState());
                if (countryOfTaxResidence) {
                    dispatch(getRequiredNationalIdentifiers());
                }
                // if response is not falsy, then request returned with success status
                if (primaryAddress?.kycStatus) {
                    dispatch({ type: 'SAVE_ONBOARDING_STATE', screen: 'InvestInsurance', params: {} });
                    if (flow !== OnboardingFlow.Pots && flow !== OnboardingFlow.Wallet) {
                        navigateToNextScreen();
                    }
                    else {
                        navigateToNextScreenForPots();
                    }
                    return;
                }
                // A user who went back to personal details has already confirmed their address; marking it unconfirmed
                // again would make the tab resume on the address step instead of where they left off
                const onboardingScreen = store.getState().invest.onboarding?.screen;
                log({ onboardingScreen });
                if (!onboardingScreen || onboardingScreen === 'InvestAddress') {
                    dispatch({ type: 'SAVE_ONBOARDING_STATE', screen: 'InvestAddress', params: {} });
                }
                if (flow !== OnboardingFlow.Pots && flow !== OnboardingFlow.Wallet) {
                    navigateToNextScreen();
                }
                else {
                    navigateToNextScreenForPots();
                }
            }
            else {
                // middleware will show error alert for us
            }
        }
        catch {
            // middleware will show error alert for us
        }
    }, [flow, navigateToNextScreen, navigateToNextScreenForPots, postData, primaryAddress?.kycStatus]);
    const onCheckPress = useCallback(() => {
        setChecked((oldValue) => !oldValue);
    }, []);
    const openRequirements = useCallback(() => {
        Links.openKycProcessLink();
    }, []);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, commonStyles.contentContainer); // 56 is Button’s height, 20 is margin to the top of Button, 16 is the space between buttons
    return (<AppView withFrame={false}>
        <View style={commonStyles.containerNoPaddingTransparent}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={paddingBottom} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title={onboardingTitleStrings.ConfirmPersonalDetails.title} subTitle={onboardingTitleStrings.ConfirmPersonalDetails.subtitle}/>
            <SettingsSection noMarginTop>
              <CardRowContainer>
                {!!title && <CardRow small smallPadding title={strings.title} info={title} testID="titleRow"/>}
                <CardRow small smallPadding title={strings.firstName} info={firstName} testID="firstNameRow"/>
                {!!middleName && (<CardRow small smallPadding title={strings.middleName} info={middleName} testID="middleNameRow"/>)}
                <CardRow small smallPadding title={strings.lastName} info={lastName} testID="lastNameRow"/>
                <CardRow small smallPadding title={strings.dob} info={dob} testID="dobRow"/>
                <CardRow small smallPadding info={country} numberOfLines={0} testID="citizenshipRow" infoStyle={styles.citizenshipInfo} title={strings.countryOfCitizenship}/>
              </CardRowContainer>
            </SettingsSection>
            <TouchableBounce onPress={onCheckPress}>
              <CardView withPadding>
                <View style={styles.row}>
                  <RadioButton type="multiple" isChecked={checked}/>
                  <Spacer.W16 />
                  <View style={styles.flex}>
                    <Text TextThin-14>I confirm that my personal details are correct.</Text>
                  </View>
                </View>
              </CardView>
            </TouchableBounce>
            <View style={styles.horizontalPadding}>
              <Text TextThin-14 Secondary>
                If the personal details provided do not match your legal information, your application will be rejected.{' '}
                <Text Text-14 Primary onPress={openRequirements} suppressHighlighting>
                  Learn more
                </Text>
              </Text>
            </View>
          </Animated.ScrollView>
          <Button brand floating title="Continue" onPress={onDone} isFetching={loading} disabled={!checked}/>
        </View>
      </AppView>);
}, ({ colors }) => ({
    hideTitleOnScroll: true,
    title: onboardingTitleStrings.ConfirmPersonalDetails.shortTitle,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    citizenshipInfo: {
        maxWidth: '60%',
        textAlign: 'right',
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    horizontalPadding: {
        paddingHorizontal: rem(16),
    },
});
export default ConfirmPersonalDetailsScreen;
